import { RefObject, useCallback, useEffect, useRef } from 'react';

import { tabClasses, tabsClasses } from '@mui/material';

import { setTimeoutBrowserFriendly } from '../../helper/browser-friendly-timeout';

export const useTabGlider = (
  tabsContainer: RefObject<HTMLDivElement>,
  activeIndex: number,
) => {
  const oldTab = useRef<HTMLElement | null>(null);
  const hasBeenInit = useRef<boolean>(false);

  // move underline indicator
  const moveIndicator = useCallback(
    (index: number) => {
      const closeTab =
        oldTab.current ??
        (tabsContainer.current?.querySelector(`.${tabsClasses.flexContainer}`)
          ?.firstElementChild as HTMLElement);

      const openTab = tabsContainer.current?.querySelector(
        `.${tabsClasses.flexContainer}`,
      )?.children[index ?? 0] as HTMLElement;

      // Activate the new tab to calculate the size of the indicator line
      removeTransitions(openTab, closeTab);
      activateTab(openTab, closeTab);

      if (closeTab && openTab) {
        if (tabsContainer.current) {
          const newTabWidth =
            openTab.offsetWidth /
            (
              tabsContainer.current.querySelector(
                `.${tabsClasses.flexContainer}`,
              ) as HTMLDivElement
            )?.offsetWidth;

          tabsContainer.current.style.setProperty(
            '--_left',
            openTab.offsetLeft + 'px',
          );
          tabsContainer.current.style.setProperty(
            '--_width',
            newTabWidth.toString(),
          );
          tabsContainer.current.style.setProperty('--_show', 'block');

          // Reactivate the old tab to perform the tab switching animation
          activateTab(closeTab, openTab);

          requestAnimationFrame(() => {
            addTransitions(openTab, closeTab);
            activateTab(openTab, closeTab);
          });
        }
      }
    },
    [tabsContainer],
  );

  const switchTab = useCallback(
    (index: number) => {
      requestAnimationFrame(() => {
        moveIndicator(index);
        oldTab.current = tabsContainer.current?.querySelector(
          `.${tabsClasses.flexContainer}`,
        )?.children[index ?? 0] as HTMLElement;
      });
    },
    [moveIndicator, tabsContainer],
  );

  const resize = useCallback(() => {
    switchTab(activeIndex);
  }, [switchTab, activeIndex]);

  useEffect(() => {
    if (!hasBeenInit.current) {
      hasBeenInit.current = true;

      // We have to wait for the initialization of the scrollable tabs
      // and its scroll buttons to ensure the correct width of the
      // tabs container (flexible container)
      setTimeoutBrowserFriendly(() => {
        switchTab(activeIndex);
      }, 100);
    } else {
      switchTab(activeIndex);
    }
  }, [activeIndex, switchTab]);

  useEffect(() => {
    window.addEventListener('resize', resize);
    return () => {
      window.removeEventListener('resize', resize);
    };
  }, [resize]);

  return { switchTab };
};

const activateTab = (activeTab: HTMLElement, inactiveTab: HTMLElement) => {
  activeTab?.classList.add(tabClasses.selected);

  if (activeTab !== inactiveTab) {
    inactiveTab.classList.remove(tabClasses.selected);
  }
};

const removeTransitions = (...tabs: HTMLElement[]) => {
  tabs.forEach((tab) => {
    tab?.style.setProperty('transition', 'none');
  });
};

const addTransitions = (...tabs: HTMLElement[]) => {
  tabs.forEach((tab) => {
    tab.style.setProperty(
      'transition',
      'color var(--transition-duration), font var(--transition-duration)',
    );
  });
};
